import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../context/AuthContext";

const fmt = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
};

const STATUS = {
  pending: "Awaiting your reply",
  change_requested: "You asked for another time",
  accepted: "Accepted — waiting for the customer to pay",
  declined: "Declined",
  converted: "Booked",
};

/** Business-side inbox for booking requests. No Stripe calls happen here. */
export const BookingRequestsPanel = ({ authHeader, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/booking-requests/business`, { headers: authHeader() });
      setRows(r.data || []);
    } catch { /* panel simply stays empty */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const act = async (id, path, body) => {
    setBusy(id);
    try {
      await axios.post(`${API}/booking-requests/${id}/${path}`, body || {}, { headers: authHeader() });
      toast.success("Sent to the customer");
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update that request");
    } finally {
      setBusy("");
    }
  };

  const open = rows.filter((r) => ["pending", "change_requested"].includes(r.status));
  if (!rows.length) return null;

  return (
    <div className="mt-6 rounded-2xl border p-5" data-testid="booking-requests-panel"
         style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-surface)" }}>
      <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
        Booking requests {open.length > 0 && <span style={{ color: "var(--gf-accent)" }}>· {open.length} waiting</span>}
      </div>
      <div className="mt-3 space-y-3">
        {rows.slice(0, 20).map((r) => (
          <div key={r.id} className="rounded-lg border p-3" data-testid={`request-row-${r.id}`}
               style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)" }}>
            <div className="text-sm" style={{ color: "var(--gf-text)" }}>
              <b>{r.service_title}</b> · {fmt(r.requested_at)}
            </div>
            <div className="text-xs mt-0.5" style={{ color: "var(--gf-text-muted)" }}>
              {r.customer_name || "Customer"} · {STATUS[r.status] || r.status}
            </div>
            {r.note && <div className="text-xs mt-1 italic" style={{ color: "var(--gf-text-soft)" }}>"{r.note}"</div>}
            {["pending", "change_requested"].includes(r.status) && (
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" data-testid={`request-accept-${r.id}`} disabled={busy === r.id}
                        onClick={() => act(r.id, "accept")}
                        className="rounded-full px-4 py-1.5 text-xs font-medium disabled:opacity-50"
                        style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                  Accept time
                </button>
                <button type="button" data-testid={`request-another-${r.id}`} disabled={busy === r.id}
                        onClick={() => act(r.id, "request-another-time", { note: window.prompt("Optional note for the customer:") || null })}
                        className="rounded-full border px-4 py-1.5 text-xs disabled:opacity-50"
                        style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
                  Request another time
                </button>
                <button type="button" data-testid={`request-decline-${r.id}`} disabled={busy === r.id}
                        onClick={() => act(r.id, "decline", { note: window.prompt("Optional note for the customer:") || null })}
                        className="rounded-full border px-4 py-1.5 text-xs disabled:opacity-50"
                        style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-danger, #b91c1c)" }}>
                  Decline request
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BookingRequestsPanel;
